package org.matthewo.pages.event

import emotion.react.css
import io.ktor.client.plugins.resources.*
import io.ktor.http.*
import js.objects.buildReadonlyRecord
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.matthewo.YEAR_PARAM
import org.matthewo.api.EventInfo
import org.matthewo.client
import org.matthewo.components.YearSelector
import org.matthewo.datasource.DataSource
import org.matthewo.hooks.useMouseEvents
import org.matthewo.hooks.useWindowSize
import org.matthewo.pages.event.tabs.*
import org.matthewo.router.TSRouteBuilder
import org.matthewo.router.div
import org.matthewo.router.toExternal
import org.matthewo.routes.API
import org.matthewo.styles.BACKGROUND_COLOR
import org.matthewo.styles.TITLE_FONT_COLOR
import org.matthewo.useElementHeader
import react.*
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.nav
import react.dom.html.ReactHTML.option
import react.dom.html.ReactHTML.select
import tanstack.react.router.*
import tanstack.router.core.ParamName
import tanstack.router.core.RoutePath
import tanstack.router.core.ToOptions
import web.cssom.*
import web.html.HTMLElement
import web.html.Hidden
import web.html.`true`

val EVENT_CODE_PARAM = ParamName("code")

val EVENT_PATH = RoutePath(YEAR_PARAM / "event" / EVENT_CODE_PARAM)

fun ToOptions.toEvent(year: Int? = null, code: String) {
    to = EVENT_PATH
    params = buildReadonlyRecord {
        if (null != year) this[YEAR_PARAM] = year.toString()
        this[EVENT_CODE_PARAM] = code
    }
}

fun TSRouteBuilder.eventRoutes() {
    TSRoute(EVENT_PATH, Event) {
        TABS.forEach { tab ->
            TSRoute(tab.routePath, tab.component)
        }
    }
    TSRoute(EVENT_TEAM_RANKING_PLOT_PATH, EventTeamRankingPlot)
    TSRoute(EVENT_MATCH_RESULT_PATH, EventMatchResult)
}

private val TABS = [
    EventTab(
        "Records",
        EventRecordsTable,
        EVENT_PATH,
        RoutePath("/"),
    ),
    EventTab(
        "Matches",
        EventMatchesTable,
    ),
    EventTab(
        "Rankings",
        EventRankingsTable,
    ),
    EventTab(
        "Playoffs",
        EventPlayoffs,
    ),
    EventTab(
        "Scatter",
        EventTeamsPeriodsScatterPlot,
    ),
    EventTab(
        "Bar",
        EventBarChart,
    ),
    EventTab(
        "Compare",
        EventCompareTeams,
    ),
    EventTab(
        "Advancement",
        EventAdvancementPointsTable,
    ),
]

data class EventTab(val title: String, val component: FC<Props>, val path: RoutePath = RoutePath(title.lowercase()), val routePath: RoutePath = path)

private val TAB_BOARDER_WIDTH = 1.px

val Event = EventTabFC<API.Event, EventInfo>({ it }) { (coroutineScope, year, eventCode, data) ->
    val navigate = useNavigate()
    val searchParams = useSearch()
    val location = useLocation()
    val matchRoute = useMatchRoute()
    val windowSize = useWindowSize()
    val years = data?.years
    val divisions = data?.divisions
    useEventHeader(years, year, eventCode, data?.name, data, location)

    div {
        css {
            width = 100.pct
            height = 100.pct
            display = Display.flex
            flexDirection = FlexDirection.column
            overflow = Overflow.hidden
        }

        nav {
            var navRef = useRef<HTMLElement>()
            ref = navRef
            val bellRef = useRef<HTMLElement>()

            val path = location.pathname

            val activeTab = TABS.firstOrNull { tab ->
                matchRoute {
                    from = EVENT_PATH
                    to = tab.path
                } != null
            } ?: TABS.first()

            val filteredTabs = useMemo(data, year) {
                if (null == data) return@useMemo []
                TABS.filter { tab ->
                    when (tab.title) {
                        "Matches" -> data.matches
                        "Rankings" -> data.rankings
                        "Playoffs" -> data.playoffs
                        "Advancement" -> data.advancement
                        else -> true
                    }
                }
            }

            css {
                position = Position.relative
                margin = 0.px
                padding = array(0.em, 1.em)
                display = Display.inlineFlex
                flexDirection = FlexDirection.row
                justifyContent = JustifyContent.left
                userSelect = None.none
                fontSize = 1.5.rem
                if (filteredTabs.isNotEmpty()) borderBottom = Border(TAB_BOARDER_WIDTH, LineStyle.solid, NamedColor.white)
            }

            useEffect(data, activeTab, filteredTabs) {
                if (null != data && activeTab !in filteredTabs) filteredTabs.firstOrNull()?.let {
                    navigate {
                        to = it.path
                        replace = true
                    }
                }
            }

            if (null != divisions) {
                select {
                    css {
                        width = 7.5.em
                        color = TITLE_FONT_COLOR
                        backgroundColor = BACKGROUND_COLOR
                        fontFamily = Globals.inherit
                        fontSize = 1.em
                        border = None.none
                    }

                    value = "DIVISIONS"
                    option {
                        disabled = true
                        hidden = Hidden.`true`
                        value = "DIVISIONS"
                        label = "Divisions"
                    }
                    val parent = divisions.firstOrNull()?.name ?: ""
                    divisions.forEachIndexed { index, division ->
                        option {
                            key = Key(division.code)
                            value = division.code
                            label = if (0 == index) division.name else division.name.removePrefix(parent).trim().removePrefix("-").trim()
                        }
                    }
                    onChange = { event ->
                        navigate {
                            params = buildReadonlyRecord {
                                this[EVENT_CODE_PARAM] = event.target.value
                            }
                            search = searchParams
                        }
                    }
                }
            }

            div {
                var tabContainerRef = useRef<HTMLElement>()
                ref = tabContainerRef

                css {
                    fontSize = 1.em
                    display = Display.inlineFlex
                    flexDirection = FlexDirection.row
                    justifyContent = JustifyContent.left
                }

                var narrow by useState(false)
                useLayoutEffect(divisions, filteredTabs, windowSize) {
                    val tabContainer = tabContainerRef.current
                    val nav = navRef.current
                    // the bell sits over the end of the tabs, so they have to end before it
                    val end = bellRef.current?.getBoundingClientRect()?.left ?: nav?.getBoundingClientRect()?.right
                    narrow = when {
                        null == tabContainer || null == end -> false
                        else -> tabContainer.getBoundingClientRect().right > end
                    }
                }

                filteredTabs.forEach { tab ->
                    val active = tab == activeTab
                    Link {
                        key = Key(tab.title)
                        css {
                            marginBottom = -TAB_BOARDER_WIDTH
                            borderWidth = TAB_BOARDER_WIDTH
                            borderStyle = if (active) LineStyle.solid else None.none
                            borderColor = array(NamedColor.white, NamedColor.white, BACKGROUND_COLOR, NamedColor.white)
                            padding = array(0.5.em, 1.em)
                            borderRadius = array(0.4.rem, 0.4.rem, 0.0.rem, 0.0.rem)
                            color = if (active) NamedColor.white else TITLE_FONT_COLOR
                            visibility = if (narrow) Visibility.hidden else Visibility.visible
                        }

                        from = EVENT_PATH
                        to = tab.path

                        +tab.title
                    }
                }

                if (narrow) {
                    select {
                        css {
                            position = Position.absolute
                            height = 100.pct + TAB_BOARDER_WIDTH
                            borderWidth = TAB_BOARDER_WIDTH
                            borderStyle = LineStyle.solid
                            borderColor = array(NamedColor.white, NamedColor.white, BACKGROUND_COLOR, NamedColor.white)
                            padding = array(0.5.em, 1.em)
                            borderRadius = array(0.4.rem, 0.4.rem, 0.0.rem, 0.0.rem)
                            color = TITLE_FONT_COLOR
                            backgroundColor = BACKGROUND_COLOR
                            fontFamily = Globals.inherit
                            fontSize = 1.em
                        }

                        value = activeTab.title
                        filteredTabs.forEach { tab ->
                            option {
                                key = Key(tab.title)
                                +tab.title
                            }
                        }
                        onChange = { event ->
                            val selected = filteredTabs.firstOrNull { it.title == event.target.value }
                            if (null != selected) {
                                navigate {
                                    from = EVENT_PATH
                                    to = selected.path
                                }
                            }
                        }
                    }
                }
            }

            if (null != data && null != year && null != eventCode) {
                div {
                    ref = bellRef
                    css {
                        position = Position.absolute
                        right = 0.5.em
                        top = 50.pct
                        transform = translatey(-50.pct)
                        display = Display.flex
                    }
                    NotificationBell {
                        this.coroutineScope = coroutineScope
                        this.year = year
                        this.code = eventCode
                        this.name = data.name
                    }
                }
            }
        }

        div {
            css {
                flexGrow = number(1.0)
                margin = 0.px
                padding = 0.px
                overflow = Auto.auto
                display = Display.flex
                flexDirection = FlexDirection.column
            }
            Outlet()
        }
    }
}

fun useEventHeader(years: List<Int>?, year: Int?, code: String?, name: String?, vararg dependencies: Any?) {
    var reloading by useState(false)
    val mouseEvents = useMouseEvents {
        it.preventDefault()
        it.stopPropagation()
        if (!reloading && null != year && null != code) {
            client.launch {
                reloading = true
                val response = client.post(API.Event.Refresh(year, code))
                if (HttpStatusCode.OK == response.status) window.location.reload()
                reloading = false
            }
        }
    }
    useElementHeader(false, *(dependencies + reloading)) {
        Link {
            css {
                color = if (reloading) NamedColor.yellow else TITLE_FONT_COLOR
                textAlign = TextAlign.center
                whiteSpace = WhiteSpace.normal
            }
            +(name ?: "")
            if (null != year && null != code) toExternal(DataSource.eventURL(year, code))
            mouseEvents()
        }
        YearSelector {
            this.years = years
        }
    }
}
